/**
 * Cookie Consent Banner for Baakas
 * GDPR-style consent banner with Baakas theme
 */

import React, { useEffect, useRef } from 'react';
import { Cookie, Shield, Settings, X } from 'lucide-react';
import { gsap } from 'gsap';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import CookieSettingsModal from './CookieSettingsModal';
import { Link } from 'react-router-dom';

const CookieConsentBanner: React.FC = () => {
  const {
    showBanner,
    acceptAll,
    acceptEssentialOnly,
    openSettings,
    isSettingsOpen,
    closeSettings,
  } = useCookieConsent();
  const bannerRef = useRef<HTMLDivElement>(null);

  // Animate in when banner shows
  useEffect(() => {
    if (showBanner && bannerRef.current) {
      gsap.fromTo(
        bannerRef.current,
        { y: 100, opacity: 0 },
        { 
          y: 0, 
          opacity: 1, 
          duration: 0.5, 
          ease: "back.out(1.7)" 
        }
      );
    }
  }, [showBanner]);

  if (!showBanner) return null;

  return (
    <>
      <div
        ref={bannerRef}
        className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-gray-200 shadow-lg md:z-[100]"
        style={{ 
          paddingBottom: 'max(16px, calc(16px + env(safe-area-inset-bottom)))',
          bottom: '64px' // Space above bottom nav on mobile
        }}
      >
            <div className="max-w-6xl mx-auto px-3 py-3 sm:px-4 sm:py-4">
              <div className="flex flex-col gap-3">
                {/* Top row - Message and Close */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Cookie className="w-4 h-4 sm:w-5 sm:h-5 text-[#da2516] flex-shrink-0" />
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                        We use cookies
                      </h3>
                    </div>
                    <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                      We use cookies to improve your experience, analyze site traffic, and personalize content.
                      By continuing to use our site, you agree to our{' '}
                      <Link
                        to="/legal/cookies"
                        className="text-[#da2516] hover:underline font-medium"
                      >
                        cookie policy
                      </Link>
                      .
                    </p>
                  </div>
                  <button
                    onClick={acceptAll}
                    className="p-1.5 sm:hidden text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Bottom row - Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={openSettings}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 text-gray-700 active:text-gray-900 font-medium text-xs sm:text-sm border border-gray-300 active:border-gray-400 rounded-lg transition-colors flex items-center justify-center gap-2 touch-manipulation"
                  >
                    <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="sm:hidden">Settings</span>
                    <span className="hidden sm:inline">Manage Preferences</span>
                  </button>
                  <button
                    onClick={acceptEssentialOnly}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 text-gray-600 active:text-gray-800 font-medium text-xs sm:text-sm active:bg-gray-50 rounded-lg transition-colors touch-manipulation"
                  >
                    Essential Only
                  </button>
                  <button
                    onClick={acceptAll}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-[#da2516] active:bg-[#b91f12] text-white font-medium text-xs sm:text-sm rounded-lg transition-colors shadow-sm touch-manipulation"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </div>

      {/* Settings Modal */}
      <CookieSettingsModal isOpen={isSettingsOpen} onClose={closeSettings} />
    </>
  );
};

export default CookieConsentBanner;
